import type { AppConfigDocument } from '@/api/appConfig';

import { compareVersions } from './appVersion';

/**
 * Карточка «Версии приложения iOS» на странице флагов (15.10.2026): `minVersion.ios` — жёсткий экран «Нужно обновить
 * приложение», `latestVersion.ios` — мягкая плашка «Вышла новая версия». Android здесь не задаётся (правило 42).
 *
 * Правила — те же, что у сервера (`users-service/internal/service/app_config_validate.go`): номер вида 1.1.2, пусто —
 * выключено, обязательная версия не выше последней. Сервер проверяет сам; здесь — чтобы админ увидел ошибку до
 * отправки, а не текстом 400.
 */

/** До трёх частей по 1–3 цифры — как на сервере. */
export const VERSION_PATTERN = /^\d{1,3}(\.\d{1,3}){0,2}$/;

export type VersionFieldError = 'format' | 'min_above_latest';

/**
 * Ошибка поля версии или `null`. `other` — значение второго поля (черновик, если его правят, иначе сохранённое).
 * Пустое поле — «выключено», ошибки нет.
 */
export function versionFieldError(field: 'min' | 'latest', value: string, other: string): VersionFieldError | null {
  const v = value.trim();
  if (!v) return null;
  if (!VERSION_PATTERN.test(v)) return 'format';
  const o = other.trim();
  if (!o || !VERSION_PATTERN.test(o)) return null;
  const [min, latest] = field === 'min' ? [v, o] : [o, v];
  return compareVersions(min, latest) === 1 ? 'min_above_latest' : null;
}

/**
 * Знает ли служба раздел `latestVersion`. Старая служба (до выката 15.10) его не отдаёт, а запись `latestVersion.ios`
 * она бы приняла и молча не применила — поэтому поле тогда выключено.
 */
export function serverKnowsLatestVersion(doc: AppConfigDocument | undefined): boolean {
  return !!doc && typeof doc.latestVersion === 'object' && doc.latestVersion !== null;
}

/**
 * Нужно ли подтверждение перед сохранением минимальной версии: она ПОДНИМАЕТСЯ (было пусто или ниже). Опустить или
 * выключить можно без вопросов — это никого не запирает.
 */
export function minVersionRaises(saved: string, next: string): boolean {
  const n = next.trim();
  if (!n) return false;
  const s = saved.trim();
  if (!s) return true;
  return compareVersions(n, s) === 1;
}
